import { useQuery } from "@apollo/client";
import type { TrackPlacement } from "analytics/eventCatalog";
import useUser from "components/common/useUser";
import trackEvent from "components/tracking/trackEvent";
import DownloadAppQrCard from "components/website/components/DownloadAppQrCard";
import Footer from "components/website/components/Footer";
import InlineInstallCard from "components/website/components/InlineInstallCard";
import LanguageSwitcher from "components/website/components/LanguageSwitcher";
import Navigation from "components/website/components/navigation/Navigation";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { getBlogTranslations } from "components/website/marketing/i18n/pages/blog";
import { EXPLORE_GUIDE } from "graphql/queries/explore";
import { t as ui } from "i18n/index";
import { buildLocalizedPath, discoverPath, isMarketingLangCode } from "i18n/urlLocale";
import { useMarketingLang } from "i18n/useMarketingLang";
import { useMemo } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { slugify } from "utils/urlUtils";
import { CircleFlag, alpha2For, displayCityName, displayCountryName } from "../b2c/explore/exploreCommon";
import { allocateArticleRails, tagKeyOf } from "./articleRails";
import styles from "./DiscoverDestinationPage.module.scss";
import {
	AIPlanBanner,
	type ArticleCard,
	Budget,
	Culture,
	DownloadBanner,
	Faq,
	fillUi,
	Food,
	ForWho,
	FunFacts,
	GuideHeadingsContext,
	GuideLangContext,
	GuideUiContext,
	bylineWithoutDate,
	Hero,
	Highlights,
	InlineReadMore,
	LocalTips,
	MobileMap,
	Neighbourhoods,
	Para,
	RelatedArticles,
	Sidebar,
	SiblingDestinations,
	WhenToGo,
	WhereToStay,
	destinationTocIds,
	useHeading,
} from "./discoverSections";

/**
 * The intro section's eyebrow + H2, split out of `DiscoverDestinationPage` into its own
 * component so `useHeading` runs INSIDE `<GuideUiContext.Provider>` / `<GuideHeadingsContext.Provider>`,
 * not above them.
 *
 * It used to be `const introHeading = useHeading(...)`, called at the top of
 * `DiscoverDestinationPage` itself, before that same component's own `return` mounts both
 * providers further down its JSX. That doesn't work: `useContext` resolves against whichever
 * provider is an ANCESTOR of the calling component in the rendered tree, and a component that
 * renders a provider as one of its own descendants is not, itself, a descendant of that
 * provider. So `useHeading`'s call to `useGuideUi()` never saw `GuideUiContext`'s value — `dict`
 * came back `undefined` on every request, `guide.section.intro.h2` (added for all seven locales
 * in febf94391, 123yb692m5g) was never looked up, and every non-English destination page kept
 * shipping the English literal "<Name>: what to expect" in its server-rendered H2 — the first
 * heading a crawler reads on the page. Verified live 2026-09-25: /nl/discover/thailand/bangkok,
 * /de/discover/japan/kyoto, and every other non-English destination page.
 *
 * Moving the hook call into a component mounted BELOW the providers — rather than moving the
 * providers themselves, which would ripple through everything else this page renders under them
 * — fixes it the way `useContext` always resolves: by tree position at render time, not by which
 * line of the file the call sits on. Same `id`, same fallback, same `vars` as before.
 *
 * `.h2` only, deliberately, even though every other `guide.section.*` entry in those locale files
 * carries a `.kicker` too. The kicker renders as its own eyebrow above the H2 and this section
 * has never had one — adding the key would put a new "Overview"/"Overzicht" line on every
 * destination page in all seven locales, English included, which is a design change and not what
 * this ticket is. The English page must stay byte-identical; only the six translated locales
 * change, and only the H2 they were already rendering in English. No `dictId` override either:
 * unlike `DiscoverCountryPage`'s own "intro" id (which deliberately points at `introCountry`
 * instead, see the comment there), the destination page owns the bare `intro` key.
 */
export function IntroHeading({ name }: { name: string }) {
	const heading = useHeading("intro", "", name ? `${name}: what to expect` : "", { vars: { name } });
	return (
		<>
			{heading.kicker && <span className={styles.sectionHeadingKicker}>{heading.kicker}</span>}
			{heading.h2 && <h2 className={styles.introHeading}>{heading.h2}</h2>}
		</>
	);
}

const DiscoverDestinationPage: React.FC<{ countrySlug: string; destinationSlug: string; initialGuide?: any; lang?: string }> = ({
	countrySlug,
	destinationSlug,
	initialGuide,
	lang: routeLang,
}) => {
	const history = useHistory();
	const { t, i18n } = useTranslation(["dashboard"]);
	const { user } = useUser();
	// The URL locale, and the only locale on this page worth trusting. `i18n.language` reads as
	// the obvious source and is not: react-i18next is initialised by `index.tsx`, the app-bundle
	// entry, and these guide pages are served by the marketing bundle, which never imports it —
	// so `useTranslation()` there hands back an uninitialised instance whose `language` is
	// `undefined`. That is what silently sent every non-English hub back to English.
	//
	// Prefers `routeLang` (the prop `guideRoutes.tsx`/`MarketingShell` hand down) over the hook,
	// same as `routeLang || "en"` everywhere else in this file — this used to derive its own
	// answer from `window.location.pathname` instead of using the prop already sitting on this
	// component, which resolved "en" on every server render regardless of the actual URL and fed
	// the back-button/breadcrumb `discoverPath()` calls below a locale-less link on first paint.
	const hookLang = useMarketingLang();
	const lang = routeLang || hookLang;

	const slug = destinationSlug || "";

	// One document per page: `geo` is "<country>/<destination>", the same key the
	// knowledge base and this page's own URL both use.
	//
	// `initialGuide` is the SSR payload — see the note in DiscoverCountryPage. Skipping the
	// query when it is set is load-bearing for the server render, not just an optimisation.
	// `routeLang` (the route's own `lang` prop), not `i18n.language`, and this used to be marked
	// KNOWN BROKEN on purpose.
	// `i18n.language` is `undefined` in the marketing bundle, so the query asked the API for
	// English on every locale: a hard load of /nl/discover/… was Dutch because the SSR loader
	// passes the locale explicitly, a client-side navigation into the same URL was not. Which
	// language you got depended on how you arrived.
	//
	// It was left alone on the grounds that changing which content is FETCHED is a product
	// decision rather than a wiring one. That reading has been overtaken twice. The SSR path
	// already fetches the locale the URL names, so this was never a choice between two
	// behaviours — it was two paths disagreeing. And the caution behind it, that a locale might
	// answer with thin or missing prose, no longer applies: the overlay corpus is down to 37
	// English-fallback strings across all six locales.
	const { data, loading } = useQuery(EXPLORE_GUIDE, {
		variables: { geo: `${countrySlug}/${slug}`, language: routeLang || "en" },
		skip: !countrySlug || !!initialGuide,
	});
	const guide = useMemo(() => initialGuide || data?.exploreGuide || null, [initialGuide, data]);

	// Nine controls on this page open the same flow, and they all reported the same event with
	// the same props — so "which one actually drives trips" was unanswerable. `placement` is the
	// canonical dimension for exactly that (TRACK_PLACEMENTS in analytics/eventCatalog.ts);
	// callers that pass nothing keep the old behaviour rather than lying about where they were.
	const planGate = usePlanGate("discover_destination", "store");

	const handleAddToTrip = (placement?: TrackPlacement, section?: string) => {
		trackEvent("open_create_trip", { path: "discover_destination", ...(placement ? { placement } : {}), ...(section ? { section } : {}) }, "discover");
		// Straight to the app for a logged-in visitor — same reasoning as the country page, see the
		// long note in DiscoverCountryPage.tsx's `handlePlanClick`. `AddToTripModal` destructures
		// `useSnackbar()`, which throws on the null context the marketing bundle gives it, and it
		// can itself open `CreateTripModal`, which additionally needs Redux.
		//
		// This page had the identical gap; 123yb692mta only happened to be reported against the
		// country one.
		//
		// Known cost, accepted: /home does not carry the destination the visitor was looking at, so
		// "add this to a trip" becomes "go to the app". Preserving that would need an app route
		// that takes a geo without a trip in the URL, and none exists today.
		if (user?._id) {
			window.location.assign("/home");
			return;
		}
		// The same gate the homepage uses, not a second answer — `history.push("/register")`
		// lived here, and that is how the two drifted apart. See planGate.tsx.
		planGate.requestPlan();
	};

	if (loading) return null;

	if (!guide) {
		return (
			<div id="stippl-discover-destination">
				{/* Hubs were the only marketing pages that never passed `langMenu` here — the switcher
				    was never mounted, not hidden or broken (123yb692mt8 / 123yb692n1c). Keep it on
				    every <Navigation> below, this not-found branch included — it is localized, and
				    `type="page"` only rewrites the current path's locale prefix, so it works on a
				    page that resolved to nothing. */}
				<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />
				<div className={styles.notFound}>
					<h1 className={styles.notFoundTitle}>{t("explore.destinationNotFound")}</h1>
					<button type="button" onClick={() => history.push(discoverPath(`/discover/${countrySlug}`, lang))} className={styles.backButton}>
						{t("explore.back")}
					</button>
				</div>
				<Footer />
			</div>
		);
	}

	if (!guide) return null;

	// `routeLang` for the same reason as `hookLang` above: `i18n.language` is undefined in the
	// marketing bundle, so both of these resolved to the English name on every locale. `displayName`
	// doubles as the `{name}` the intro section's `<IntroHeading>` fills its H2 with, below — see
	// that component's own doc comment for why the heading is computed there and not here.
	const displayName = displayCityName(guide.name, routeLang || "en");
	const displayCountry = displayCountryName(guide.country, routeLang || "en");

	const openCountry = () => history.push(discoverPath(`/discover/${guide.countrySlug}`, lang));
	const sidebarCountry = { name: guide.name, mapEmbed: guide.mapEmbed, countryCode: (alpha2For(guide.country) || "").toLowerCase() };

	// UI-chrome dictionary (kickers, H2s, table copy) for the section chrome shared with the
	// blog articles — see the `GuideUiContext` doc comment in discoverSections.tsx. The blog
	// article pages have resolved this from their own locale since PR #1046; this page never
	// provided one, so every `ui("blog.section.*", …)` call inside it (including "Weather by
	// month") fell back to its English literal on every non-English hub.
	// `routeLang` (the route's own `lang` prop), not `i18n.language`. `MarketingShell.tsx:69`
	// derives the locale from the router's `location.pathname` — which resolves on the server as
	// well as the client — and passes it to every route component as `lang` (it is what line 70
	// already feeds the nav and footer translations). `i18n.language` is not that: there is no
	// i18next instance in the marketing bundle at all during SSR — the console says so,
	// `NO_I18NEXT_INSTANCE` — so it resolved to English and `getBlogTranslations` returned the
	// English map. The page still read as translated because the guide prose arrives
	// pre-localised from the SSR loader, so the chrome was the only thing exposed:
	// /it/discover/japan shipped Italian prose under "Weather by month" and "Best time". Verified
	// live on 2026-09-08 before this fix: zero Italian dictionary strings on the page.
	const tr = getBlogTranslations(routeLang || i18n.language || "en");

	// `guide.updatedAt` is an ISO `YYYY-MM-DD` off the dossier's own frontmatter, carried through
	// by `build_guide_documents.py` (123yb69326q) — replacing a byline that hardcoded "August
	// 2026" literally, in all seven locales, English included, on every /discover page. Formatted
	// with `toLocaleDateString`, not a dictionary lookup: it localises the month name for free (no
	// `guide.chrome.month.*` key needed here), which is the whole reason this is carried as a raw
	// date rather than pre-rendered in the knowledge base build.
	//
	// `routeLang`, not `i18n.language` or `lang` — same reasoning as `tr` immediately above: there
	// is no i18next instance in the marketing bundle during SSR, and `lang` is derived from
	// `window.location.pathname`, which does not exist yet at that point either.
	//
	// Guarded: `toLocaleDateString` throws on a malformed BCP-47 tag, and a bad locale must
	// degrade to the no-date byline below rather than take the page down with it — the same
	// failure mode as a dossier with no `updated_at` at all (2 of 471 today), on purpose.
	let formattedUpdatedAt: string | undefined;
	if (guide.updatedAt) {
		try {
			const updated = new Date(guide.updatedAt);
			if (!Number.isNaN(updated.getTime())) {
				formattedUpdatedAt = updated.toLocaleDateString(routeLang || "en", { year: "numeric", month: "long" });
			}
		} catch {
			formattedUpdatedAt = undefined;
		}
	}

	// `guide.chrome.byline` bakes "Updated {date} · " into one string across all seven locale
	// files rather than exposing the date clause as its own key — so there is no dictionary key
	// for "no date, still byline" to look up. Adding one would mean writing and
	// shipping a translation in six files this session does not own, for a ticket that is
	// explicitly about not inventing what those files say. Instead, the no-date byline is derived
	// from the SAME translated string: every one of the seven `guide.chrome.byline` values is
	// two clauses joined by " · ", with `{date}` always inside the first one (verified against
	// every locale file), so dropping "the clause that mentions {date}" before filling is a
	// structural operation on the string the translators already wrote, not a new piece of copy.
	// A geo whose dossier has never been checked (2 today) gets "Written by the Stippl editors" —
	// no invented date, and no stray leading " · " either. There is no read time: it was a
	// hardcoded 9 on every destination, and was dropped rather than computed (123yb694ta8).
	const BYLINE_EN = "Updated {date} · Written by the Stippl editors";
	const bylineTemplate = ui(tr, "guide.chrome.byline", BYLINE_EN);
	const byline = formattedUpdatedAt ? fillUi(bylineTemplate, { date: formattedUpdatedAt }) : bylineWithoutDate(bylineTemplate, BYLINE_EN);

	// Same source for article-card hrefs, narrowed rather than defaulted to `i18n.language`:
	// `buildLocalizedPath` is typed against `MARKETING_LANG_CODES`, and `i18n.language` is the
	// value that is wrong during SSR (see above), not just a looser fallback. NOT `lang` above
	// either — that one is derived from `window.location.pathname`, which doesn't exist yet
	// during SSR.
	const rawLang = routeLang || "";
	const articleLang = isMarketingLangCode(rawLang) ? rawLang : "en";

	// Destination-level articles carry country and destination, mirroring the guide URL these
	// cards sit on. See the note in DiscoverCountryPage for why the old leading continent
	// segment went — it also had to be looked up through the hardcoded country map here, because continent is
	// not on CityData, which is exactly the kind of indirection a URL should not depend on.
	// `/blog/<slug>`, the one URL space articles live in. The nested
	// `/blog/destinations/...` shape these used to build was removed with its routes
	// (2026-08-25) so new articles share the URL space of the 412 already indexed ones.
	//
	// Locale-prefixed with `articleLang` — see the matching note in DiscoverCountryPage: the
	// backend now resolves each rail entry's `slug` against the visitor's locale
	// (graphql/schemas/explore.ts), so this only keeps the link on the same side of the locale
	// prefix as the page it's on.
	const buildArticleHref = (article: { slug?: string; title: string }) =>
		buildLocalizedPath(article.slug ? `/blog/${article.slug}` : `/blog/${slugify(article.title)}`, articleLang);

	// The number the backend now returns takes over from the English-snapshot `read` string on a
	// non-English hub — see `localizeArticleRail` in graphql/schemas/explore.ts. A rail entry
	// whose translation didn't resolve has no `readingMinutes` and keeps its (English) `read`.
	const localizedArticles: ArticleCard[] = (guide.articles || []).map((a: any) =>
		typeof a.readingMinutes === "number" ? { ...a, read: `${a.readingMinutes} ${ui(tr, "blog.article.minAbbr", "min")}` } : a
	);

	// Give each rail its own picks instead of letting them repeat — see articleRails.ts
	// (123yb691zu8).
	const [citiesRail, foodRoutesRail] = allocateArticleRails<ArticleCard>(localizedArticles, [
		{ filter: (a) => tagKeyOf(a) === "Cities" },
		{ filter: (a) => tagKeyOf(a) === "Food" || tagKeyOf(a) === "Routes" },
	]);

	// Per-geo eyebrow/H2 overrides from the knowledge base's copy layer. Provided at the root
	// so every section can opt in by its own id instead of taking a prop it usually ignores.
	// Undefined for a geo with no copy file, which is almost all of them today.
	return (
		<GuideUiContext.Provider value={tr}>
			{/* `articleLang`, not `lang`/`routeLang` directly — same narrowed, SSR-correct value the
			    hrefs above already use, and the one `RichText`'s markdown-link branch needs to
			    localise an in-body link (123yb692y18). Not `useMarketingLang()` — see the
			    `GuideLangContext` doc comment in discoverSections.tsx. */}
			<GuideLangContext.Provider value={articleLang}>
				<GuideHeadingsContext.Provider value={guide.headings}>
					<div id="stippl-discover-destination" className={styles.page}>
						{/* Mirrors `discoverGuideHead` — see the note on DiscoverCountryPage. */}
						<Helmet>
							<title>{guide.title || (displayName ? `${displayName}, ${displayCountry} travel guide | Stippl` : "Discover | Stippl")}</title>
							{(guide.metaDescription || guide.tagline) && <meta name="description" content={guide.metaDescription || guide.tagline} />}
						</Helmet>

						<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

						<Hero
							name={guide.name}
							tagline={guide.tagline}
							heroPhoto={guide.heroPhoto}
							mapEmbed={guide.mapEmbed}
							mapMarkers={guide.mapMarkers}
							quickFacts={guide.quickFacts}
							kind={guide.kind}
							country={guide.country}
							displayName={displayName}
							displayCountry={displayCountry}
							stipplStats={guide.stipplStats}
							nutshell={guide.nutshell}
							bestTime={guide.bestTime}
							neighbourhoodsCount={guide.neighbourhoods.length}
							onPlan={() => handleAddToTrip("header_cta", "hero")}
							onOpenCountry={openCountry}
							countrySlug={guide.countrySlug}
						/>

						<div className={styles.contentWrap}>
							<div className={styles.contentGrid}>
								<div>
									<div id="intro" className={styles.introBlock}>
										<div className={styles.introHeader}>
											<span className={styles.flagBadge}>
												<CircleFlag country={guide.country} size={26} />
											</span>
											<span className={styles.introLabel}>{byline}</span>
										</div>
										<IntroHeading name={displayName} />
										<div className={styles.introFacts}>
											{guide.intro.map((para) => (
												<Para key={para.slice(0, 24)}>{para}</Para>
											))}
										</div>
									</div>

									<MobileMap guide={guide} />

									{/* work/install-card-mobile.md's A3: after the intro, before the reader is
									    into the guide's sections proper. Mobile/tablet only (CSS-hidden on
									    desktop, see InlineInstallCard.scss). `displayName` here is the
									    destination itself, not its parent country — matches DownloadBanner's own
									    choice, below, to fill "Plan {country} in Stippl" with the destination
									    name. A destination page always resolves one, so this is never the
									    no-country case. */}
									<InlineInstallCard country={displayName} locale={routeLang || i18n.language || "en"} />

									<ForWho name={guide.name} forWho={guide.forWho} lessIdealFor={guide.lessIdealFor} />
									<WhenToGo guide={guide} onPlan={() => handleAddToTrip("card_cta", "when_to_go")} />
									<InlineReadMore
										articles={citiesRail}
										label={fillUi(ui(tr, "blog.section.railReadMore", "Read more {name} stories"), { name: guide.name })}
										buildHref={buildArticleHref}
									/>
									<AIPlanBanner name={guide.name} onPlan={() => handleAddToTrip("inline_banner", "ai_plan_banner")} />
									<Highlights guide={guide} />
									<Neighbourhoods guide={guide} onPlan={() => handleAddToTrip("card_cta", "neighbourhoods")} />
									<WhereToStay guide={guide} />
									<Food name={guide.name} food={guide.food} onPlan={() => handleAddToTrip("card_cta", "food")} />
									<InlineReadMore articles={foodRoutesRail} label={ui(tr, "blog.section.railAlsoLike", "You might also like")} buildHref={buildArticleHref} />
									{/* Optional on a destination guide — both self-guard to null when the guide carries
									    no culture/budget data, which is most of them. Kept in the same slot as the
									    country page: after Food, before the trivia/tips tail. */}
									<Culture culture={guide.culture || []} etiquette={guide.etiquette || { do: [], dont: [] }} />
									<Budget name={guide.name} budget={guide.budget || { tiers: [], breakdown: [] }} onPlan={() => handleAddToTrip("card_cta", "budget")} />
									<FunFacts guide={guide} />
									<LocalTips guide={guide} />
									<Faq guide={guide} />

									<div className={styles.feedbackCard}>
										<p className={styles.feedbackText}>
											{ui(
												tr,
												"guide.chrome.editorNote",
												"Every guide is written by the Stippl editors and checked against the trips our travellers actually plan. Spotted something out of date?"
											)}{" "}
											<span className={styles.feedbackHighlight}>{ui(tr, "guide.chrome.letUsKnow", "Let us know")}</span>.
										</p>
									</div>
								</div>

								<Sidebar
									name={sidebarCountry.name}
									mapEmbed={sidebarCountry.mapEmbed}
									mapMarkers={guide.mapMarkers}
									countryCode={sidebarCountry.countryCode}
									sections={destinationTocIds(guide)}
									onPlan={() => handleAddToTrip("sidebar_menu", "sidebar")}
									partOfCountry={{
										name: displayCountry,
										countryCode: sidebarCountry.countryCode,
										href: discoverPath(`/discover/${guide.countrySlug}`, lang),
										onClick: openCountry,
									}}
								/>
							</div>
						</div>

						<DownloadBanner guide={guide} />
						<RelatedArticles
							heading={
								<>
									More {guide.name} & {guide.country} stories
								</>
							}
							articles={localizedArticles}
							buildHref={buildArticleHref}
							kicker={ui(tr, "blog.article.relatedKicker", "Read on")}
							readLabel={ui(tr, "blog.article.readSuffix", "read")}
						/>

						{/* "More in {country}". Draft placement: Luuk places it on the preview. One self-contained
						    block, so moving it is moving these lines. Hidden when there are no siblings. */}
						{!!guide.siblings?.length && (
							<div className={styles.contentWrap}>
								<SiblingDestinations
									country={displayCountry}
									countrySlug={guide.countrySlug}
									siblings={guide.siblings.map((s: any) => ({ ...s, name: displayCityName(s.name, routeLang || "en") }))}
								/>
							</div>
						)}

						<Footer />
						{/* Floating install card (bugs/blog-install-card-never-renders.md,
						    work/owned-link-install-cta.md): unconditional, including alongside
						    DownloadBanner above — it reveals at ~500px (~5% into the page) while that
						    banner sits near the very end, so gating it off would hand the install
						    affordance to only readers who reach the bottom. Reach beats tidiness; do
						    not re-add a gate. No non-default props: this page scrolls `window` (not an
						    inner container) at every viewport, so the default reveal="scroll" fires;
						    `.stippl-footer .footer-row` exists for the compact-viewport watcher; and
						    the only other fixed/sticky element near the viewport (the TOC sidebar) sits
						    at the right of the content grid, clear of the card's default bottom-left
						    dock — so neither `desktopRight` nor `aboveBottomNav` is needed. Verified via
						    Playwright against production before this was added. */}
						<DownloadAppQrCard />

						<PlanGateModal
							gate={planGate}
							title={ui(tr, "guide.auth.destination", "Create a free account to add this to a trip")}
							loginTitle={ui(tr, "guide.auth.login", "Log in to plan with AI")}
						/>
					</div>
				</GuideHeadingsContext.Provider>
			</GuideLangContext.Provider>
		</GuideUiContext.Provider>
	);
};

export default DiscoverDestinationPage;
